/**
 * Simple countdown inside <lsg-promotional-popup>
 * Expects a child element .js-timer with data-timer in seconds.
 */
class PromotionalPopup extends HTMLElement {

  constructor() {
      super();

      this.disabled = this.dataset.disabled ?? false;
      this.timeToFire = parseInt(this.dataset.timeToFire, 10);
      this.scrollTrigger = parseInt(this.dataset.scrollTrigger, 10);

      this.timerEl = this.querySelector(".js-timer");
      this.timerElMin = this.querySelector(".js-timer-min");
      this.timerElSec = this.querySelector(".js-timer-sec");
      this.dateEl = this.querySelector(".js-date");
      this.applyDiscountEl = this.querySelector(".js-apply-discount");

      this.overlay  = this.querySelector("#promotional-popup-overlay");
      this.card     = this.querySelector(".promotional-popup__card");
      this.closeBtns= this.querySelectorAll(".promotional-popup__close");

      this._escHandler = (e) => { if (e.key === "Escape") this.close(); };
      this._overlayClick = (e) => {
        if (!this.card || !this.card.contains(e.target)) this.close();
      };
  }

  connectedCallback() {
    if (this.dateEl) {
      this.handleDate();
    }
    if (this.timerEl) {
      this.handleTimer();
    }
    if (this.applyDiscountEl) {
      this.handleApplyDiscount();
    }

    this.showPopup();

    if (this.overlay) {
      this.overlay.setAttribute(
        "aria-hidden",
        this.overlay.classList.contains("hidden") ? "true" : "false"
      );
      this.overlay.addEventListener("click", this._overlayClick);
    }
    this.closeBtns.forEach(btn => btn.addEventListener("click", () => this.close()));
    document.addEventListener("keydown", this._escHandler);
  }


  showPopup() {
    if (this.disabled) return;
    if (this.isBlocked()) return;

    this.handleTimeTrigger();
    
    this.handleScrollTrigger();
  }


  handleTimeTrigger() {
    if (this.timeToFire) {
      setTimeout(() => this.open(), this.timeToFire * 1000);
    }
  }

  handleScrollTrigger() {
    if (!Number.isFinite(this.scrollTrigger) || this.scrollTrigger <= 0) return;
    let triggered = false;

    window.addEventListener('scroll', () => {
      if (triggered) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentPercent = (scrollTop / docHeight) * 100;

      if (currentPercent >= this.scrollTrigger) {
        triggered = true;
        this.open();
      }
    });
  }

  disconnectedCallback() {
    if (this.overlay) this.overlay.removeEventListener("click", this._overlayClick);
    document.removeEventListener("keydown", this._escHandler);
  }

  open() {
    if (this.isBlocked()) return;
    if (!this.overlay) return;
    this.overlay.classList.remove("hidden");
    this.overlay.setAttribute("aria-hidden", "false");
  }

  close() {
    if (!this.overlay) return;
    this.setBlockCookie();
    this.overlay.classList.add("hidden");
    this.overlay.setAttribute("aria-hidden", "true");
  }

  get cookieName() {
    return this.dataset.cookieKey || 'lsg_promotional_popup_blocked';
  }

  isBlocked() {
    return document.cookie.split('; ').some(c => c.startsWith(this.cookieName + '='));
  }

  setBlockCookie() {
    document.cookie = `${this.cookieName}=1; Max-Age=259200; Path=/`;
  }

  handleDate() {
    const el = this.dateEl;
    if (!el) return;
  
    const dateStr = el.dataset.date;
    const timeStr = el.dataset.dateTime;
  
    const timeZone = 'America/Chicago';
  
    const parseTarget = (dateStr, timeStr) => {
      const d = dateStr.trim();
      const t = (timeStr || '00:00').trim();
      let y, m, day, hour, minute;
  
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
        [y, m, day] = d.split('-').map(Number);
      } else {
        const mdy = d.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (!mdy) return NaN;
        [, m, day, y] = mdy.map(Number);
      }
  
      [hour, minute] = t.split(':').map(Number);
  
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      });
  
      const utcGuess = Date.UTC(y, m - 1, day, hour, minute, 0);
      const parts = Object.fromEntries(
        dtf.formatToParts(new Date(utcGuess))
           .filter(p => p.type !== 'literal')
           .map(p => [p.type, p.value])
      );
  
      const asZoneLocalUTC = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );
  
      const desiredLocalUTC = Date.UTC(y, m - 1, day, hour, minute, 0);
      const diff = desiredLocalUTC - asZoneLocalUTC;
  
      return utcGuess + diff;
    };
  
    const targetMs = parseTarget(dateStr, timeStr);
    if (!targetMs || isNaN(targetMs)) return;
  
    const daysEl = el.querySelector('.js-days');
    const hoursEl = el.querySelector('.js-hours');
    const minutesEl = el.querySelector('.js-minutes');
    const secondsEl = el.querySelector('.js-seconds');
    const pad = n => String(n).padStart(2, '0');
  
    const tick = () => {
      const now = Date.now();
      let delta = targetMs - now;
      if (delta <= 0) {
        el.style.display = 'none';
        clearInterval(timer);
        return;
      }
  
      const days = Math.floor(delta / 86400000);
      delta -= days * 86400000;
  
      const hours = Math.floor(delta / 3600000);
      delta -= hours * 3600000;
  
      const minutes = Math.floor(delta / 60000);
      delta -= minutes * 60000;
  
      const seconds = Math.floor(delta / 1000);
  
      daysEl.textContent = days;
      hoursEl.textContent = pad(hours);
      minutesEl.textContent = pad(minutes);
      secondsEl.textContent = pad(seconds);
    };
  
    tick();
    const timer = setInterval(tick, 1000);
  }

  handleApplyDiscount() {
    const el = this.applyDiscountEl;
    if (!el) return;

    el.addEventListener("click", (e) => {
      e.preventDefault();

      const code = (el.dataset.discountCode || "").trim();

      const current = this.applyDiscountEl.dataset.url;

      let url = current;

      if (code) {
        url = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(current)}`;
      }

      this.setBlockCookie();

      location.assign(url);
    });
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return {mins:`${mins}`, sec: `${secs.toString().padStart(2, "0")}`};
  }

  handleTimer() {
    if (!this.timerEl) return;

    let timeLeft = parseInt(this.timerEl.dataset.timer || "0", 10);
    let interval;

    const updateTimer = () => {
      this.timerElMin.textContent = this.formatTime(timeLeft).mins;
      this.timerElSec.textContent = this.formatTime(timeLeft).sec;
      if (timeLeft > 0) {
        timeLeft -= 1;
      } else {
        clearInterval(interval);
        this.close();
      }
    };

    updateTimer();
    interval = setInterval(updateTimer, 1000);
  }
}

customElements.define("lsg-promotional-popup", PromotionalPopup);
